import { useEffect, useState } from 'react';

import AutoScalingStateFilter from '~/shared/components/filters/AutoScalingStateFilter';
import FiltersDisclosure from '~/shared/components/filters/FiltersDisclosure';
import SearchInstance from '~/shared/components/filters/search/SearchInstance';
import SearchVolumeId from '~/shared/components/filters/search/SearchVolumeId';
import SearchVolumeTags from '~/shared/components/filters/search/SearchVolumeTags';
import SearchClusterName from '~/shared/components/filters/search/SearchClusterName';
import { SelectOption } from '~/shared/utils/types';

interface FilterPanelContentProps {
  panelOpen?: boolean;
  pendingAutoscaleState: string[];
  setPendingAutoscaleState: (state: string[]) => void;
  pendingClusterNameFilters: SelectOption[];
  setPendingClusterNameFilters: (filters: SelectOption[]) => void;
  pendingInstanceFilters: SelectOption[];
  setPendingInstanceFilters: (filters: SelectOption[]) => void;
  pendingVolumeIdFilters: SelectOption[];
  setPendingVolumeIdFilters: (filters: SelectOption[]) => void;
  pendingVolumeTagFilters: SelectOption[];
  setPendingVolumeTagFilters: (filters: SelectOption[]) => void;
}

export default function FilterPanelContent({
  panelOpen = false,
  pendingAutoscaleState,
  setPendingAutoscaleState,
  pendingClusterNameFilters,
  setPendingClusterNameFilters,
  pendingInstanceFilters,
  setPendingInstanceFilters,
  pendingVolumeIdFilters,
  setPendingVolumeIdFilters,
  pendingVolumeTagFilters,
  setPendingVolumeTagFilters,
}: FilterPanelContentProps) {
  const [isClusterNameDisclosureOpen, setIsClusterNameDisclosureOpen] = useState(false);
  const [isInstanceDisclosureOpen, setIsInstanceDisclosureOpen] = useState(false);
  const [isVolumeIdDisclosureOpen, setIsVolumeIdDisclosureOpen] = useState(false);
  const [isVolumeTagDisclosureOpen, setIsVolumeTagDisclosureOpen] = useState(false);

  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (!panelOpen) {
      setHasInitialized(false);
      return;
    }

    if (hasInitialized) {
      return;
    }

    // Check which disclosures should be open based on filters
    const shouldOpenClusterName = pendingClusterNameFilters.length > 0;
    const shouldOpenInstance = pendingInstanceFilters.length > 0;
    const shouldOpenVolumeId = pendingVolumeIdFilters.length > 0;
    const shouldOpenVolumeTags = pendingVolumeTagFilters.length > 0;

    const hasAnyOpen =
      isClusterNameDisclosureOpen ||
      isInstanceDisclosureOpen ||
      isVolumeIdDisclosureOpen ||
      isVolumeTagDisclosureOpen ||
      shouldOpenClusterName ||
      shouldOpenInstance ||
      shouldOpenVolumeId ||
      shouldOpenVolumeTags;

    if (shouldOpenClusterName) {
      setIsClusterNameDisclosureOpen(true);
    }
    if (shouldOpenInstance) {
      setIsInstanceDisclosureOpen(true);
    }
    if (shouldOpenVolumeId) {
      setIsVolumeIdDisclosureOpen(true);
    }
    if (shouldOpenVolumeTags) {
      setIsVolumeTagDisclosureOpen(true);
    }

    if (!hasAnyOpen) {
      setIsClusterNameDisclosureOpen(true);
    }

    setHasInitialized(true);
  }, [
    panelOpen,
    hasInitialized,
    pendingClusterNameFilters.length,
    pendingInstanceFilters.length,
    pendingVolumeIdFilters.length,
    pendingVolumeTagFilters.length,
    isClusterNameDisclosureOpen,
    isInstanceDisclosureOpen,
    isVolumeIdDisclosureOpen,
    isVolumeTagDisclosureOpen,
  ]);

  return (
    <div className="flex-1 overflow-y-auto pb-4">
      <AutoScalingStateFilter
        pendingAutoscaleState={pendingAutoscaleState}
        setPendingAutoscaleState={setPendingAutoscaleState}
      />
      <FiltersDisclosure
        title="Cluster Name"
        count={pendingClusterNameFilters?.length > 0 ? pendingClusterNameFilters.length : undefined}
        className="px-8 border-b border-gray-300"
        open={isClusterNameDisclosureOpen}
        onOpenChange={setIsClusterNameDisclosureOpen}
      >
        <SearchClusterName
          filters={pendingClusterNameFilters}
          setFilters={(newSelections) => {
            setPendingClusterNameFilters(newSelections.filter((f) => f.group === 'clusterName'));
          }}
          isDisclosureOpen={isClusterNameDisclosureOpen}
        />
      </FiltersDisclosure>

      <FiltersDisclosure
        title="Instance ID"
        count={pendingInstanceFilters.length > 0 ? pendingInstanceFilters.length : undefined}
        className="px-8 border-b border-gray-300"
        open={isInstanceDisclosureOpen}
        onOpenChange={setIsInstanceDisclosureOpen}
      >
        <SearchInstance
          filters={pendingInstanceFilters}
          setFilters={(newSelections) => {
            setPendingInstanceFilters(newSelections.filter((f) => f.group === 'instanceId'));
          }}
          isDisclosureOpen={isInstanceDisclosureOpen}
        />
      </FiltersDisclosure>
      <FiltersDisclosure
        title="Volume ID"
        count={pendingVolumeIdFilters.length > 0 ? pendingVolumeIdFilters.length : undefined}
        className="px-8 border-b border-gray-300 "
        open={isVolumeIdDisclosureOpen}
        onOpenChange={setIsVolumeIdDisclosureOpen}
      >
        <SearchVolumeId
          filters={pendingVolumeIdFilters}
          setFilters={(newSelections) =>
            setPendingVolumeIdFilters(newSelections.filter((f) => f.group === 'volumeId'))
          }
          isDisclosureOpen={isVolumeIdDisclosureOpen}
        />
      </FiltersDisclosure>
      <FiltersDisclosure
        title="Volume Tags"
        count={pendingVolumeTagFilters.length > 0 ? pendingVolumeTagFilters.length : undefined}
        className="px-8 border-b border-gray-300"
        open={isVolumeTagDisclosureOpen}
        onOpenChange={setIsVolumeTagDisclosureOpen}
      >
        <SearchVolumeTags
          filters={pendingVolumeTagFilters}
          setFilters={(newSelections) =>
            setPendingVolumeTagFilters(newSelections.filter((f) => f.group.startsWith('tag:')))
          }
          isDisclosureOpen={isVolumeTagDisclosureOpen}
        />
      </FiltersDisclosure>
    </div>
  );
}
